'use client'

import { useEffect } from 'react'

/**
 * Check if error is recoverable by a full page reload.
 * These errors typically occur when the server has been rebuilt
 * and the client has stale cached chunks or RSC data.
 */
function isStaleClientError(error) {
    const message = error?.message || ''
    const name = error?.name || ''

    return (
        message.includes('Connection closed') ||
        message.includes('Failed to fetch') ||
        message.includes('Load failed') ||
        name === 'ChunkLoadError' ||
        message.includes('ChunkLoadError') ||
        message.includes('Loading chunk') ||
        message.includes('NEXT_STATIC_GEN_BAILOUT')
    )
}

/**
 * Report error to server for email notification
 */
async function reportError(error) {
    try {
        await fetch('/api/log-client-error', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                message: error?.message || 'Unknown error',
                stack: error?.stack || '',
                url: typeof window !== 'undefined' ? window.location.href : '',
                userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
                timestamp: new Date().toISOString(),
                type: 'global'
            })
        })
    } catch (e) {
        console.error('[Global Error Boundary] Failed to report error:', e)
    }
}

export default function GlobalError({ error, reset }) {
    useEffect(() => {
        console.error('[Global Error Boundary]', error)

        // Auto-reload for stale client errors
        if (isStaleClientError(error)) {
            console.log('[Global Error Boundary] Stale client detected, reloading page...')
            window.location.reload()
            return
        }

        // Report non-stale errors for email notification
        reportError(error)
    }, [error])

    // Global error must render its own html/body - can't use Next.js components
    return (
        <html>
            <body>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '100vh',
                    padding: '2rem',
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                    textAlign: 'center'
                }}>
                    <a href="/" style={{ marginBottom: '2rem' }}>
                        <img
                            src="/images/hive-logo-gray.png"
                            alt="hive modern"
                            width={120}
                            height={60}
                        />
                    </a>
                    <h1 style={{ fontSize: '1.875rem', fontWeight: 300, marginBottom: '0.75rem' }}>
                        We hit a snag
                    </h1>
                    <p style={{ color: '#6b7280', marginBottom: '2rem', maxWidth: '28rem' }}>
                        Something unexpected happened. Please try again, or return to our homepage to continue shopping.
                    </p>
                    <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', justifyContent: 'center' }}>
                        <button
                            onClick={() => reset()}
                            style={{
                                display: 'inline-block',
                                padding: '0.75rem 1.5rem',
                                backgroundColor: '#000000',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '2px',
                                fontSize: '1rem',
                                fontWeight: '500',
                                cursor: 'pointer',
                                textAlign: 'center'
                            }}
                        >
                            Try again
                        </button>
                        <a
                            href="/"
                            style={{
                                display: 'inline-block',
                                padding: '0.75rem 1.5rem',
                                backgroundColor: '#ffffff',
                                color: '#000000',
                                border: '1px solid #000000',
                                borderRadius: '2px',
                                fontSize: '1rem',
                                fontWeight: '500',
                                textDecoration: 'none',
                                cursor: 'pointer',
                                textAlign: 'center'
                            }}
                        >
                            Go to homepage
                        </a>
                    </div>
                </div>
            </body>
        </html>
    )
}